import { Select } from "@components/v2";
import { LoadingIndicator } from "@components/v2/mini-loading-indicator/mini-loading-indicator";
import { LearnMoreTooltip } from "@features/sales-v3/top-level-filters/components/learn-more-tooltip";
import { Partner } from "@north-beam/nb-client-side";
import { type FetchPartnersCVEConsent_me_partnerCVEConsent } from "@north-beam/nb-client-side/nb-api-graphql-generated";

import { PartnerCveCheckbox } from "./partner-cve-checkbox";

interface CvePartnersListProps {
  cveConsentForPartners: FetchPartnersCVEConsent_me_partnerCVEConsent[];
  preferredCvePartner: Partner | null;
  canToggleCveConsent: boolean;
  onChangePreferredCvePartner(partner: Partner): void;
  onChangeConsent(partner: Partner, consent: boolean): void;
  getPartnerDisplayName(partner: Partner): string;
  isLoadingCveConsent: boolean;
  isUsingCrossPublisher?: boolean;
}

export function CvePartnersList({
  cveConsentForPartners,
  preferredCvePartner,
  canToggleCveConsent,
  onChangePreferredCvePartner,
  onChangeConsent,
  getPartnerDisplayName,
  isLoadingCveConsent,
  isUsingCrossPublisher = false,
}: CvePartnersListProps) {
  if (isLoadingCveConsent) {
    return <LoadingIndicator />;
  }

  const totalPartnersWithConsent = cveConsentForPartners.filter(
    (partnerConsent) => partnerConsent.hasConsent,
  ).length;

  const showWarningMessageForTiktokWhenSelected =
    isUsingCrossPublisher && totalPartnersWithConsent > 1;

  return (
    <div className="ml-7">
      <div className="italic font-normal text-v2-primary">
        Must be enabled both below <b>and</b> on the selected platforms. Once
        complete, data will begin flowing within approximately 48 hours.
      </div>
      {!isUsingCrossPublisher && (
        <>
          <div className="mt-2 text-xs font-semibold text-v2-primary">
            Primary Attribution Platform
            <LearnMoreTooltip
              title="Primary platform"
              content="Choose which platform is your primary source of impressions for the Enhanced model."
            />
          </div>
          <Select
            key="preferred_cve_partner"
            placeholder="Select Platform"
            buttonClassName="w-48 mt-1 rounded-md"
            value={preferredCvePartner}
            options={cveConsentForPartners.map((partnerConsent) => ({
              value: partnerConsent.partner,
              label: getPartnerDisplayName(partnerConsent.partner),
              disabled: !partnerConsent.hasConnectedAccounts,
            }))}
            onChange={(updatedPreferredCvePartner) =>
              updatedPreferredCvePartner &&
              onChangePreferredCvePartner(updatedPreferredCvePartner)
            }
            disabled={!canToggleCveConsent}
          />
        </>
      )}
      <div className="flex flex-col mt-1">
        {cveConsentForPartners.map((partnerCveConsent) => (
          <PartnerCveCheckbox
            key={partnerCveConsent.partner}
            partner={partnerCveConsent.partner}
            partnerDisplayName={getPartnerDisplayName(
              partnerCveConsent.partner,
            )}
            hasConsent={partnerCveConsent.hasConsent}
            hasConnectedAccounts={partnerCveConsent.hasConnectedAccounts}
            canToggleCveConsent={canToggleCveConsent}
            onChangeConsent={onChangeConsent}
            warningMessage={
              partnerCveConsent.partner === Partner.TIKTOK &&
              showWarningMessageForTiktokWhenSelected &&
              partnerCveConsent.hasConsent ? (
                <>
                  Tiktok Clicks + Deterministic Views data is temporarily
                  unavailable in the multi publisher view. <br />
                  <i>
                    Tiktok data will be available in this view in the coming
                    weeks
                  </i>
                </>
              ) : (
                ""
              )
            }
          />
        ))}
      </div>
    </div>
  );
}
